import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, ArrowRight, Play, Pause, Search, Music, Loader2, X, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ReplacementPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  // Fullscreen track picker
  const [pickerRequestId, setPickerRequestId] = useState(null);
  const [pickerGenre, setPickerGenre] = useState('');
  const [allTracks, setAllTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [trackSearch, setTrackSearch] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [replaceLoading, setReplaceLoading] = useState(null);
  const audioRef = useRef(null);

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState(null);
  const [replaceDone, setReplaceDone] = useState(false);

  const loadRequests = async () => {
    try {
      const url = filter === 'all' ? `${API}/api/replacement/requests` : `${API}/api/replacement/requests?status=${filter}`;
      const r = await axios.get(url, { withCredentials: true });
      setRequests(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadRequests(); }, [filter]);

  // Approve and immediately open picker
  const handleApproveAndPick = async (id, genre) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/api/replacement/approve/${id}`, { approved: true, admin_note: '' }, { withCredentials: true });
      openTrackPicker(id, genre);
      loadRequests();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
    setActionLoading(null);
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/api/replacement/approve/${id}`, { approved: false, admin_note: '' }, { withCredentials: true });
      loadRequests();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
    setActionLoading(null);
  };

  // Fullscreen Track Picker
  const openTrackPicker = async (requestId, genre) => {
    setPickerRequestId(requestId);
    setPickerGenre(genre || '');
    setTrackSearch('');
    setTracksLoading(true);
    try {
      const r = await axios.get(`${API}/api/tracks/?limit=500`, { withCredentials: true });
      const data = r.data;
      setAllTracks(Array.isArray(data) ? data : (data.tracks || []));
    } catch { setAllTracks([]); }
    setTracksLoading(false);
  };

  const closeTrackPicker = () => {
    setPickerRequestId(null);
    setAllTracks([]);
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
  };

  const togglePlay = (trackId, url) => {
    if (playingId === trackId) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioRef.current) audioRef.current.pause();
      const audio = new Audio(url);
      audio.play();
      audio.onended = () => setPlayingId(null);
      audioRef.current = audio;
      setPlayingId(trackId);
    }
  };

  const showReplaceConfirm = (requestId, track) => {
    const req = requests.find(r => r.id === requestId);
    setConfirmModal({
      requestId,
      track,
      clientName: req?.user_name || req?.user_email || 'Cliente',
      clientEmail: req?.user_email || '',
      originalTrack: req?.original_track_name || '',
      genre: req?.original_genre || pickerGenre,
    });
  };

  const executeReplace = async () => {
    if (!confirmModal || replaceLoading) return;
    const { requestId, track } = confirmModal;
    setReplaceLoading(track.id);
    try {
      await axios.post(`${API}/api/replacement/replace/${requestId}`, { new_track_id: track.id }, { withCredentials: true });
      setReplaceDone(true);
      closeTrackPicker();
      loadRequests();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
      setConfirmModal(null);
    }
    setReplaceLoading(null);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
    setReplaceDone(false);
  };

  // Group and filter tracks
  const filteredTracks = allTracks.filter(t =>
    !trackSearch ||
    (t.title || '').toLowerCase().includes(trackSearch.toLowerCase()) ||
    (t.artist || '').toLowerCase().includes(trackSearch.toLowerCase())
  );

  const tracksByGenre = {};
  filteredTracks.forEach(t => {
    const g = t.genre || 'Sin Genero';
    if (!tracksByGenre[g]) tracksByGenre[g] = [];
    tracksByGenre[g].push(t);
  });

  const sortedGenreKeys = Object.keys(tracksByGenre).sort((a, b) => {
    if (a.toLowerCase() === pickerGenre.toLowerCase()) return -1;
    if (b.toLowerCase() === pickerGenre.toLowerCase()) return 1;
    return a.localeCompare(b);
  });

  const statusColors = {
    pending: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Pendiente' },
    approved: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', label: 'Rechazado' },
    completed: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', label: 'Completado' },
  };

  if (loading) return <p className="text-white/40 py-8 text-center">Cargando solicitudes...</p>;

  const pending = requests.filter(r => r.status === 'pending');
  const approved = requests.filter(r => r.status === 'approved');

  return (
    <div data-testid="replacement-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">Reemplazos de Track</h2>
          {pending.length > 0 && (
            <span className="px-2.5 py-1 bg-yellow-500/20 rounded-full text-xs font-bold text-yellow-400" data-testid="replacement-pending-badge">
              {pending.length} pendiente{pending.length > 1 ? 's' : ''}
            </span>
          )}
          {approved.length > 0 && (
            <span className="px-2.5 py-1 bg-blue-500/20 rounded-full text-xs font-bold text-blue-400" data-testid="replacement-approved-badge">
              {approved.length} aprobado{approved.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/70" data-testid="replacement-filter">
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="completed">Completados</option>
            <option value="rejected">Rechazados</option>
          </select>
          <button onClick={loadRequests} className="p-2 hover:bg-white/10 rounded-lg text-white/40" data-testid="replacement-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {requests.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <AlertTriangle className="w-8 h-8 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No hay solicitudes de reemplazo</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(req => {
            const sc = statusColors[req.status] || statusColors.pending;
            return (
              <div key={req.id} className={`bg-white/[0.03] border ${sc.border} rounded-xl overflow-hidden`} data-testid={`replacement-request-${req.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 ${sc.bg} ${sc.text} rounded text-[10px] font-bold uppercase`}>{sc.label}</span>
                        <span className="text-[10px] text-white/30">{new Date(req.created_at).toLocaleString()}</span>
                      </div>
                      <h3 className="text-base font-semibold text-white mb-1 truncate">{req.original_track_name}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
                        <span className="flex items-center gap-1">
                          Cliente: <strong className="text-white/70">{req.user_name || req.user_email}</strong>
                          {req.client_total_replacements > 1 && (
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${
                              req.client_total_replacements >= 3 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                            }`} data-testid={`client-replacement-count-${req.id}`}>
                              {req.client_total_replacements}x reemplazos
                            </span>
                          )}
                        </span>
                        <span>Genero: <strong className="text-[#E8720C]">{req.original_genre || 'N/A'}</strong></span>
                        <span>Licencia: <strong className="text-white/70">{req.license_type?.toUpperCase()}</strong></span>
                        <span>Monto: <strong className="text-white/70">${req.amount?.toFixed(2)}</strong></span>
                      </div>
                      {req.reason && <p className="mt-2 text-xs text-white/40 italic">Razon: {req.reason}</p>}
                      {req.new_track_name && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <span className="text-white/40">Reemplazado por:</span>
                          <ArrowRight className="w-3 h-3 text-green-400" />
                          <span className="text-green-400 font-semibold">{req.new_track_name}</span>
                        </div>
                      )}
                      {req.admin_note && <p className="mt-1 text-xs text-white/40">Nota admin: {req.admin_note}</p>}
                      <p className="mt-1 text-[10px] text-white/25">Solicitado por: {req.requested_by_name || req.requested_by}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 shrink-0">
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApproveAndPick(req.id, req.original_genre || '')}
                            disabled={actionLoading === req.id}
                            className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                            data-testid={`approve-replacement-${req.id}`}
                          >
                            {actionLoading === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Aprobar y Reemplazar
                          </button>
                          <button
                            onClick={() => handleReject(req.id)}
                            disabled={actionLoading === req.id}
                            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                            data-testid={`reject-replacement-${req.id}`}
                          >
                            <XCircle className="w-3 h-3" />
                            Rechazar
                          </button>
                        </>
                      )}
                      {req.status === 'approved' && (
                        <button
                          onClick={() => openTrackPicker(req.id, req.original_genre || '')}
                          className="px-4 py-2 bg-[#E8720C]/20 hover:bg-[#E8720C]/30 text-[#E8720C] rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                          data-testid={`select-replacement-${req.id}`}
                        >
                          <Music className="w-3 h-3" />
                          Seleccionar Track
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ FULLSCREEN TRACK PICKER MODAL ═══ */}
      {pickerRequestId && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex flex-col" data-testid="track-picker-modal"
          onKeyDown={e => { if (e.key === 'Escape') closeTrackPicker(); }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0a0a0e] shrink-0">
            <div className="flex items-center gap-4">
              <button onClick={closeTrackPicker} className="p-2 rounded-lg hover:bg-white/10 text-white/50" data-testid="picker-back">
                <X className="w-5 h-5" />
              </button>
              <div>
                <h3 className="text-lg font-bold">Seleccionar Track de Reemplazo</h3>
                <p className="text-xs text-[#E8720C]">Genero original: {pickerGenre || 'Todos'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <input className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 w-72 focus:outline-none focus:border-[#E8720C]/50"
                placeholder="Buscar por nombre o artista..."
                value={trackSearch} onChange={e => setTrackSearch(e.target.value)} data-testid="picker-search" autoFocus />
              <button onClick={closeTrackPicker}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-sm font-bold text-white/70">
                Cancelar
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {tracksLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-[#E8720C]" /></div>
            ) : sortedGenreKeys.length === 0 ? (
              <div className="flex items-center justify-center h-full text-white/30">No hay tracks disponibles</div>
            ) : (
              sortedGenreKeys.map(genre => {
                const isMatch = genre.toLowerCase() === pickerGenre.toLowerCase();
                return (
                  <div key={genre}>
                    <div className={`sticky top-0 z-10 px-6 py-2.5 border-b border-white/[0.06] flex items-center gap-3 ${isMatch ? 'bg-[#E8720C]/10' : 'bg-[#111114]'}`}>
                      <span className={`text-sm font-bold ${isMatch ? 'text-[#E8720C]' : 'text-white/60'}`}>{genre}</span>
                      <span className="text-xs text-white/30">{tracksByGenre[genre].length} tracks</span>
                      {isMatch && <span className="px-2 py-0.5 bg-[#E8720C]/20 text-[#E8720C] rounded text-[9px] font-bold">GENERO ORIGINAL</span>}
                    </div>
                    {tracksByGenre[genre].map((t, i) => (
                      <div key={t.id}
                        className={`flex items-center gap-4 px-6 py-3.5 transition-colors border-b border-white/[0.04] group ${
                          i % 2 === 0 ? 'bg-transparent hover:bg-white/[0.03]' : 'bg-white/[0.015] hover:bg-white/[0.04]'
                        }`} data-testid={`picker-track-${t.id}`}>
                        {t.audio_preview_url ? (
                          <button onClick={(e) => { e.stopPropagation(); togglePlay(t.id, t.audio_preview_url); }}
                            className="w-8 h-8 rounded-full bg-white/5 hover:bg-[#E8720C]/20 flex items-center justify-center shrink-0">
                            {playingId === t.id ? <Pause className="w-3.5 h-3.5 text-[#E8720C]" /> : <Play className="w-3.5 h-3.5 text-white/50 ml-0.5" />}
                          </button>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0"><Music className="w-3.5 h-3.5 text-white/20" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white/80">{t.title}</span>
                          <span className="text-sm text-white/30 ml-3">{t.artist}</span>
                        </div>
                        <span className="text-xs text-white/30 w-16 text-right shrink-0">{t.bpm > 0 ? `${t.bpm} BPM` : ''}</span>
                        <button
                          onClick={() => showReplaceConfirm(pickerRequestId, t)}
                          disabled={replaceLoading === t.id}
                          className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-lg text-xs font-bold opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50 shrink-0 flex items-center gap-1.5"
                          data-testid={`replace-with-${t.id}`}
                        >
                          {replaceLoading === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                          Reemplazar
                        </button>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ═══ CONFIRMATION MODAL ═══ */}
      {confirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => !replaceLoading && !replaceDone && closeConfirmModal()}>
          <div className="w-full max-w-lg mx-4 bg-[#0c0c10] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-testid="replace-confirm-modal">

            {replaceDone ? (
              <div className="p-8 text-center" data-testid="replace-success">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Reemplazo Enviado!</h3>
                <p className="text-sm text-white/60 mb-1">El track ha sido reemplazado exitosamente.</p>
                <p className="text-sm text-white/60 mb-6">Se envio el correo de confirmacion a <strong className="text-white">{confirmModal.clientEmail}</strong></p>
                <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-xl mb-6">
                  <p className="text-xs text-green-400/80">
                    <strong>{confirmModal.originalTrack}</strong> → <strong>{confirmModal.track.title}</strong>
                  </p>
                </div>
                <button onClick={closeConfirmModal}
                  className="px-8 py-3 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl font-bold text-sm transition-colors"
                  data-testid="replace-success-close">
                  Cerrar
                </button>
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-white/10 bg-[#0a0a0e]">
                  <h3 className="text-lg font-bold text-white">Confirmar Reemplazo</h3>
                  <p className="text-xs text-white/40 mt-0.5">Revisa el correo que se le enviara al cliente</p>
                </div>

                <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
                  {/* Client Profile + Summary */}
                  <div className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/10 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-[#E8720C]/20 flex items-center justify-center text-[#E8720C] font-bold text-sm shrink-0">
                      {(confirmModal.clientName || 'C')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white">{confirmModal.clientName}</p>
                      <p className="text-xs text-white/40">{confirmModal.clientEmail}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[9px] text-white/30 uppercase">Reemplazo</p>
                      <p className="text-xs text-white/50 line-through">{confirmModal.originalTrack}</p>
                      <p className="text-xs text-[#E8720C] font-semibold">{confirmModal.track.title}</p>
                    </div>
                  </div>

                  {/* Real Email Preview in iframe */}
                  <div className="rounded-xl overflow-hidden border border-[#E8720C]/20" data-testid="email-preview-container">
                    <div className="px-4 py-2 bg-[#E8720C]/10 flex items-center gap-2">
                      <Send className="w-3 h-3 text-[#E8720C]" />
                      <span className="text-[10px] text-[#E8720C] font-bold uppercase tracking-wider">Correo que recibira el cliente</span>
                    </div>
                    <iframe
                      src={`${API}/api/replacement/preview/success-email-dynamic?buyer_name=${encodeURIComponent(confirmModal.clientName)}&old_track=${encodeURIComponent(confirmModal.originalTrack)}&new_track=${encodeURIComponent(confirmModal.track.title)}&genre=${encodeURIComponent(confirmModal.genre)}`}
                      className="w-full bg-[#050507]"
                      style={{ height: '480px', border: 'none' }}
                      title="Preview del correo"
                      data-testid="email-preview-iframe"
                    />
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a0e] flex items-center justify-between">
                  <button onClick={closeConfirmModal} disabled={replaceLoading}
                    className="px-5 py-2.5 text-sm text-white/50 hover:text-white/80 transition-colors disabled:opacity-30">
                    Cancelar
                  </button>
                  <button onClick={executeReplace} disabled={replaceLoading}
                    className="px-8 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl font-bold text-sm flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="confirm-replace-send">
                    {replaceLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {replaceLoading ? 'Enviando...' : 'Enviar Reemplazo'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReplacementPanel;
